import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Lock, Upload, Hourglass, CreditCard, Apple, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PaymentMethodsPicker from "@/components/PaymentMethodsPicker";
import { isNativeIOS } from "@/lib/platform";
import IAP from "@/lib/iap";

/**
 * AccessLimitReachedCard
 *
 * Drop-in lock card shown when a limited-access student tries to load
 * content that's been clipped to zero (limit reached, mode locked, or
 * payment pending).
 *
 * Accepts a fully-resolved `accessState` (from `/api/student/access-status`)
 * so the page doesn't refetch.
 */
export default function AccessLimitReachedCard({ accessState, reason }) {
  const [paySettings, setPaySettings] = useState(null);
  const [systems, setSystems] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  useEffect(() => {
    api.get("/payment-settings/public").then((r) => setPaySettings(r.data)).catch(() => {});
    api.get("/payment-systems/public").then((r) => setSystems(r.data || [])).catch(() => {});
  }, []);

  const pending = accessState?.is_pending_review;
  // PayMongo is only active when enabled + link configured.
  const paymongoActive = !!(paySettings?.paymongo_enabled && paySettings?.paymongo_payment_link);
  const hasManualSystems = (systems || []).length > 0;

  return (
    <>
      <Card className="p-8 sm:p-10 border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50" data-testid="access-limit-reached-card">
        <div className="flex flex-col items-center text-center">
          <div className="h-16 w-16 rounded-full grid place-items-center mb-4 bg-amber-100 text-amber-700 ring-4 ring-amber-200">
            {pending ? <Hourglass className="w-8 h-8" strokeWidth={1.5} /> : <Lock className="w-8 h-8" strokeWidth={1.5} />}
          </div>
          {pending ? (
            <>
              <h2 className="font-display text-2xl font-bold text-slate-900">Your payment proof has been submitted</h2>
              <p className="text-slate-700 mt-2 max-w-md">
                It is waiting for admin verification. Full course access will be unlocked after admin confirmation.
              </p>
            </>
          ) : (
            <>
              <h2 className="font-display text-2xl font-bold text-slate-900">Limited Access Limit Reached</h2>
              <p className="text-slate-700 mt-2 max-w-md">
                {reason || (
                  <>You have used all <span className="font-semibold">{accessState?.question_limit ?? "your free"}</span> limited-access questions. Complete your payment to unlock the full course.</>
                )}
              </p>
              <div className="mt-5 flex flex-wrap gap-2 justify-center">
                {/* Apple compliance: on native iOS render StoreKit-only CTAs. */}
                {isNativeIOS() ? (
                  <IosLockedIapCtas accessState={accessState} />
                ) : (
                  <>
                    {(paymongoActive || hasManualSystems) && (
                      <Button onClick={() => setPickerOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="lock-view-methods-btn">
                        <CreditCard className="w-4 h-4 mr-2" /> View Payment Methods
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => { /* upload handled via dashboard banner */ window.location.href = "/student"; }}
                      className="border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="lock-upload-btn">
                      <Upload className="w-4 h-4 mr-2" /> Upload Receipt
                    </Button>
                  </>
                )}
              </div>
              {!isNativeIOS() && !paymongoActive && !hasManualSystems && (
                <p className="text-xs text-amber-800 mt-3">No payment methods enabled yet — please contact your administrator.</p>
              )}
              {!isNativeIOS() && (
                <p className="text-[11px] text-slate-500 mt-4">Use the Upload Receipt button on your dashboard after paying.</p>
              )}
            </>
          )}
        </div>
      </Card>

      <PaymentMethodsPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onUploadReceipt={() => { window.location.href = "/student"; }}
      />
    </>
  );
}

/** Apple-compliant CTAs shown on the lock card when a native-iOS student
 *  hits the limited-access quota. Mirrors the banner IAP flow. */
function IosLockedIapCtas({ accessState }) {
  const [product, setProduct] = useState(null);
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await IAP.init();
        const pid = accessState?.pricing?.product_id;
        if (!pid) return;
        await IAP.ensureProducts([pid]);
        for (let i = 0; i < 20 && !cancelled; i++) {
          const p = IAP.getProduct(pid);
          if (p) { setProduct(p); break; }
          await new Promise((r) => setTimeout(r, 500));
        }
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [accessState?.pricing?.product_id]);
  const purchase = async () => {
    if (!accessState?.course_id) { toast.error("Select a course before subscribing."); return; }
    setBusy(true);
    try {
      await IAP.buy({ courseId: accessState.course_id, productId: accessState?.pricing?.product_id });
      toast.success("Purchase complete — full access unlocked!");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.message || "Could not start the Apple purchase.");
    } finally { setBusy(false); }
  };
  const restore = async () => {
    setRestoring(true);
    try {
      await IAP.restore({ courseId: accessState?.course_id });
      toast.success("Purchase restored — full access unlocked.");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.message || "Could not restore purchases.");
    } finally { setRestoring(false); }
  };
  return (
    <>
      <Button onClick={purchase} disabled={busy || restoring || !accessState?.pricing?.product_id}
        className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="lock-iap-subscribe-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Apple className="w-4 h-4 mr-2" />}
        {busy ? "Starting…" : `Subscribe — ${product?.price || "Access"}`}
      </Button>
      <Button onClick={restore} disabled={busy || restoring} variant="outline"
        className="border-slate-300 text-slate-800" data-testid="lock-iap-restore-btn">
        {restoring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
        {restoring ? "Restoring…" : "Restore Purchases"}
      </Button>
      {!product && (
        <p className="text-[11px] text-amber-800 w-full text-center mt-1">Loading App Store pricing…</p>
      )}
    </>
  );
}
